import { HealthSourceNote } from "@/components/health/HealthSourceInfo";
import { useState, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { useAuth } from "@/lib/AuthContext";
import { Capacitor } from "@capacitor/core";
import { Camera as CapCamera, CameraResultType, CameraSource } from "@capacitor/camera";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScanLine, Camera, ImageIcon, Sparkles, Loader2, RotateCcw, Check } from "lucide-react";
import { toast } from "sonner";
import { todayLocalDateStr } from "@/lib/dateUtils";

// When running inside a Capacitor native shell (iOS .ipa / Android .apk)
// the browser `<input type="file" capture>` flow is unreliable inside
// WKWebView. We swap to the native Camera plugin so users get a real
// system camera/photo-picker, then route the result through the same
// shrinkImage() pipeline as the web path.
const IS_NATIVE = Capacitor.isNativePlatform();

/**
 * Resize a File/Blob to fit inside maxDim px and re-encode as JPEG so the
 * base64 payload sent to GPT-5.2 vision is bounded (most phone photos are
 * 5+ MB raw — we cap around ~400 KB JPEG which is plenty for food recognition).
 */
function shrinkImage(file, maxDim = 1280, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read image"));
    reader.onload = () => {
      img.onerror = () => reject(new Error("Could not decode image"));
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const ratio = Math.min(maxDim / width, maxDim / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL("image/jpeg", quality);
        resolve({ dataUrl, base64: dataUrl.split(",")[1], mime: "image/jpeg" });
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function MealScanner({ open, onOpenChange }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const cameraInputRef = useRef(null);
  const libraryInputRef = useRef(null);

  // step: "capture" | "preview" | "scanning" | "result"
  const [step, setStep] = useState("capture");
  const [preview, setPreview] = useState(null); // { dataUrl, base64, mime }
  const [result, setResult] = useState(null); // { food_name, portion, calories, ... }
  // Initial meal-time defaults to "now" — recomputed on each dialog open via
  // the onOpenChange wrapper below.
  const [mealTime, setMealTime] = useState(() => {
    const d = new Date();
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  });

  // Reset all state when the dialog opens so the next session starts fresh.
  // Done in the onOpenChange wrapper rather than a `useEffect([open])` to
  // avoid the lint rule against derived state in effects.
  const handleOpenChange = (next) => {
    if (next) {
      setStep("capture");
      setPreview(null);
      setResult(null);
      const d = new Date();
      setMealTime(
        `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`,
      );
    }
    onOpenChange(next);
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) {
      toast.error("Please use a JPEG, PNG, or WebP image.");
      return;
    }
    try {
      const shrunk = await shrinkImage(file);
      setPreview(shrunk);
      setStep("preview");
    } catch (e) {
      toast.error(e.message || "Could not load image.");
    }
  };

  // Native (iOS / Android Capacitor) — pop the system camera/photo picker
  // via the plugin, then convert the returned data URL to a File so we
  // can reuse the same shrink + base64 pipeline as the web path.
  const handleNativeCapture = async (source) => {
    try {
      const photo = await CapCamera.getPhoto({
        quality: 82,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: source, // CameraSource.Camera or CameraSource.Photos
        correctOrientation: true,
      });
      if (!photo?.dataUrl) return;
      const res = await fetch(photo.dataUrl);
      const blob = await res.blob();
      const file = new File([blob], "meal.jpg", { type: blob.type || "image/jpeg" });
      await handleFile(file);
    } catch (e) {
      // Plugin throws when the user cancels the picker — swallow silently
      // unless it's a real error.
      const msg = String(e?.message || e);
      if (!/cancel/i.test(msg)) toast.error(msg || "Camera unavailable.");
    }
  };

  const scanMutation = useMutation({
    mutationFn: () =>
      base44.integrations.Core.ScanMeal({
        image_base64: preview.base64,
        mime_type: preview.mime,
      }),
    onMutate: () => setStep("scanning"),
    onSuccess: (data) => {
      if (!data.food_name || data.food_name.toLowerCase() === "unknown") {
        toast.error("Couldn't identify food in that photo. Try a clearer shot.");
        setStep("preview");
        return;
      }
      setResult(data);
      // Auto-save the moment the AI returns a valid identification.
      // The user lands on the result screen with the meal already in
      // their log — they can tweak any field inline and re-save, or just
      // close the dialog.
      saveMutation.mutate(data);
    },
    onError: (e) => {
      toast.error(e?.response?.data?.detail || e.message || "AI scan failed. Try again.");
      setStep("preview");
    },
  });

  const saveMutation = useMutation({
    mutationFn: (override) => {
      const r = override || result;
      return base44.entities.MealLog.create({
        user_id: user.id,
        completed_date: todayLocalDateStr(),
        food_name: r.food_name,
        portion: r.portion || "1 serving",
        meal_time: mealTime || null,
        calories: Number(r.calories) || 0,
        protein_g: Number(r.protein_g) || 0,
        carbs_g: Number(r.carbs_g) || 0,
        fats_g: Number(r.fats_g) || 0,
        photo_url: r.photo_url || "",
        source: "ai_scan",
        completed: true,
      });
    },
    onSuccess: (saved, override) => {
      // Track the MealLog id so the "Update" mutation can patch it in place
      // (instead of creating a duplicate row) when the user edits the AI's
      // estimate.
      const r = override || result;
      setResult({ ...r, _logId: saved?.id });
      setStep("result");
      qc.invalidateQueries({ queryKey: ["meal_logs"] });
      qc.invalidateQueries({ queryKey: ["MealLog"] });
      toast.success(`${r.food_name} logged to today's meals.`);
    },
    onError: () => {
      toast.error("Could not save meal log.");
      setStep("preview");
    },
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      base44.entities.MealLog.update(result._logId, {
        food_name: result.food_name,
        portion: result.portion || "1 serving",
        meal_time: mealTime || null,
        calories: Number(result.calories) || 0,
        protein_g: Number(result.protein_g) || 0,
        carbs_g: Number(result.carbs_g) || 0,
        fats_g: Number(result.fats_g) || 0,
      }),
    onSuccess: () => {
      toast.success("Meal updated.");
      qc.invalidateQueries({ queryKey: ["meal_logs"] });
      qc.invalidateQueries({ queryKey: ["MealLog"] });
      onOpenChange(false);
    },
    onError: () => toast.error("Could not update meal."),
  });

  const setField = (k, v) => setResult((r) => ({ ...r, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="meal-scanner-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanLine className="w-5 h-5 text-sky-400" />
            Scan a Meal
          </DialogTitle>
          <DialogDescription>
            Snap a photo of your plate and GPT-5.2 vision will estimate the macros.
          </DialogDescription>
        </DialogHeader>
        <p className="text-xs text-slate-400">AI estimate ? check foods, portions and values before saving.</p>
        <HealthSourceNote topic="food" />

        {step === "capture" && (
          <div className="space-y-3 py-2">
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              capture="environment"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="meal-scanner-camera-input"
            />
            <input
              ref={libraryInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="meal-scanner-library-input"
            />
            <Button
              type="button"
              size="lg"
              className="w-full bg-sky-500 hover:bg-sky-400 text-white"
              onClick={() =>
                IS_NATIVE
                  ? handleNativeCapture(CameraSource.Camera)
                  : cameraInputRef.current?.click()
              }
              data-testid="meal-scanner-take-photo"
            >
              <Camera className="w-5 h-5 mr-2" />
              Take a photo
            </Button>
            <Button
              type="button"
              variant="outline"
              size="lg"
              className="w-full"
              onClick={() =>
                IS_NATIVE
                  ? handleNativeCapture(CameraSource.Photos)
                  : libraryInputRef.current?.click()
              }
              data-testid="meal-scanner-choose-photo"
            >
              <ImageIcon className="w-5 h-5 mr-2" />
              Choose from library
            </Button>
            <p className="text-[11px] text-white/40 text-center pt-2">
              JPEG, PNG, or WebP · resized to 1280px before upload
            </p>
          </div>
        )}

        {(step === "preview" || step === "scanning") && preview && (
          <div className="space-y-3">
            <div className="relative rounded-xl overflow-hidden bg-black/40">
              <img
                src={preview.dataUrl}
                alt="meal preview"
                className="w-full max-h-72 object-contain"
                data-testid="meal-scanner-preview"
              />
              {step === "scanning" && (
                <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center gap-2">
                  <Loader2 className="w-7 h-7 text-sky-400 animate-spin" />
                  <p className="text-sm text-white/80">Analyzing food…</p>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep("capture")}
                disabled={step === "scanning"}
                data-testid="meal-scanner-retake"
              >
                <RotateCcw className="w-4 h-4 mr-1" /> Retake
              </Button>
              <Button
                type="button"
                className="flex-1 bg-sky-500 hover:bg-sky-400 text-white"
                onClick={() => scanMutation.mutate()}
                disabled={step === "scanning"}
                data-testid="meal-scanner-analyze"
              >
                <Sparkles className="w-4 h-4 mr-2" /> Analyze with AI
              </Button>
            </div>
          </div>
        )}

        {step === "result" && result && (
          <div className="space-y-3">
            <div
              className="flex items-center gap-2 bg-emerald-500/15 border border-emerald-400/40 rounded-lg px-3 py-2"
              data-testid="meal-scanner-saved-banner"
            >
              <Check className="w-4 h-4 text-emerald-300 shrink-0" />
              <span className="text-sm font-semibold text-emerald-200">
                Saved to today&apos;s meals
              </span>
              <span
                className="ml-auto text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/5 text-white/60"
                data-testid="meal-scanner-confidence"
              >
                AI:{" "}
                <span
                  className={
                    result.confidence === "high"
                      ? "text-emerald-300 font-bold"
                      : result.confidence === "low"
                      ? "text-amber-300 font-bold"
                      : "text-sky-300 font-bold"
                  }
                >
                  {result.confidence}
                </span>
                {result.model_used && (
                  <span
                    className="ml-1 normal-case opacity-70"
                    title={
                      result.model_used.endsWith("mini")
                        ? "Simple-photo path — routed to the cheaper mini model"
                        : "Complex-photo path — full vision model"
                    }
                  >
                    · {result.model_used}
                  </span>
                )}
              </span>
            </div>
            {preview && (
              <img
                src={preview.dataUrl}
                alt=""
                className="w-full max-h-40 object-contain rounded-lg bg-black/40"
              />
            )}
            <p className="text-[11px] text-white/50 leading-relaxed">
              Edit any field below if the AI estimate is off, then tap{" "}
              <span className="font-semibold text-white/80">Save changes</span>.
              Otherwise just close — the meal is already in your log.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-2">
                <Label className="text-xs">Food</Label>
                <Input
                  value={result.food_name}
                  onChange={(e) => setField("food_name", e.target.value)}
                  data-testid="meal-scanner-food"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Portion</Label>
                <Input
                  value={result.portion}
                  onChange={(e) => setField("portion", e.target.value)}
                  data-testid="meal-scanner-portion"
                />
              </div>
              <div>
                <Label className="text-xs">Calories</Label>
                <Input
                  type="number"
                  value={result.calories}
                  onChange={(e) => setField("calories", e.target.value)}
                  data-testid="meal-scanner-calories"
                />
              </div>
              <div>
                <Label className="text-xs">Time</Label>
                <Input
                  type="time"
                  value={mealTime}
                  onChange={(e) => setMealTime(e.target.value)}
                  data-testid="meal-scanner-time"
                />
              </div>
              <div>
                <Label className="text-xs text-emerald-300">Protein (g)</Label>
                <Input
                  type="number"
                  value={result.protein_g}
                  onChange={(e) => setField("protein_g", e.target.value)}
                  data-testid="meal-scanner-protein"
                />
              </div>
              <div>
                <Label className="text-xs text-sky-300">Carbs (g)</Label>
                <Input
                  type="number"
                  value={result.carbs_g}
                  onChange={(e) => setField("carbs_g", e.target.value)}
                  data-testid="meal-scanner-carbs"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-amber-300">Fat (g)</Label>
                <Input
                  type="number"
                  value={result.fats_g}
                  onChange={(e) => setField("fats_g", e.target.value)}
                  data-testid="meal-scanner-fats"
                />
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={updateMutation.isPending}
                data-testid="meal-scanner-done"
              >
                Done
              </Button>
              <Button
                type="button"
                className="flex-1 bg-sky-500 hover:bg-sky-400 text-white"
                onClick={() => updateMutation.mutate()}
                disabled={updateMutation.isPending || !result._logId}
                data-testid="meal-scanner-save"
              >
                {updateMutation.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Save changes
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
